import { Box, Button, Checkbox, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, FormControlLabel, FormGroup, IconButton, InputAdornment, Radio, TextField, Typography } from "@mui/material";
import { useCallback, useEffect, useState } from "react";
import SearchIcon from "@mui/icons-material/Search";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import EditIcon from '@mui/icons-material/Edit';
import HelpIcon from '@mui/icons-material/Help';

export default function SelectWithModal(props: SelectWithModalProps) {

    const [openModal, setOpenModal] = useState(false);
    const [selected, setSelected] = useState(props.defaultSelected || []);
    const [labelSelect, setLabelSelect] = useState<any[]>([])

    // Manipuladores do componente
    const handleOpenModal = useCallback(() => {
        if (props.disabled) return
        setOpenModal(true)
    }, [props.disabled])

    const handleClick = useCallback((item: any, event: any) => {
        let newSelected = [...selected];

        if (props.multiple) {
            let index = selected.findIndex((i: any) => i.id === item.id);

            if (index >= 0 && event.target.checked === false) {
                newSelected.splice(index, 1);
            } else {
                newSelected.push(item);
            }
        }

        if (!props.multiple) {
            newSelected.splice(0, newSelected.length);
            newSelected.push(item)
        }

        setSelected(newSelected);
    }, [selected, props.multiple])

    const handleFillTextValue = useCallback(() => {
        if (labelSelect && labelSelect.length <= 0) {
            return props.emptyLabel;
        }

        const firstName = labelSelect[0]?.nome;

        if (labelSelect.length > 1) {
            const qtdRemaining = labelSelect.length - 1;
            return `${firstName}, + ${qtdRemaining}`;
        }

        return firstName;
    }, [labelSelect, props.emptyLabel])
    //


    // Manipuladores de ações do Dialog
    const handleClear = useCallback(() => {
        setSelected([]);
        setLabelSelect([])
        setOpenModal(false);
        props.onClear();
    }, [props])

    const handleSubmit = useCallback(() => {
        setOpenModal(false)
        setLabelSelect(selected)
        props.onChange(selected)
    }, [props, selected])
    //

    useEffect(() => {
        const pushValue = props.defaultSelected || []
        setSelected(pushValue)
        setLabelSelect(pushValue)
    }, [props.defaultSelected])

    return (
        <>
            {
                props.type === "field" &&
                <TextField
                    label={props.label}
                    variant='outlined'
                    value={handleFillTextValue()}
                    onClick={handleOpenModal}
                    disabled={props.disabled}
                    fullWidth={props.fullWidthInput}
                    slotProps={{
                        inputLabel: { shrink: true },
                        input: {
                            readOnly: true,
                            endAdornment: <InputAdornment position="end"><ArrowDropDownIcon /></InputAdornment>
                        }
                    }}
                />
            }

            {
                props.type === "icon" &&
                <>
                    <Box display="flex" flexDirection="column">
                        <Typography fontSize={12}>{props.label}</Typography>
                        <Typography fontSize={18}>{handleFillTextValue()}</Typography>
                    </Box>
                    <IconButton
                        aria-label="edit"
                        size="small"
                        sx={{ marginLeft: 1 }}
                        onClick={handleOpenModal}
                    >
                        <EditIcon fontSize="small" />
                    </IconButton>
                </>
            }

            <Dialog
                open={openModal}
                onClose={() => setOpenModal(false)}
                maxWidth={"xs"}
                fullWidth={true}
            >
                <DialogTitle>{props.titleModal}</DialogTitle>
                <DialogContent sx={{ overflow: "hidden" }}>
                    <TextField
                        value={props.keywordValue}
                        margin="normal"
                        fullWidth
                        label="Pesquisar"
                        slotProps={{
                            input: {
                                endAdornment: <InputAdornment position="end"><SearchIcon /></InputAdornment>,
                            }
                        }}
                        onChange={(e) => {
                            props.onKeywordChange(e.target.value)
                        }}
                    />
                    <Box maxHeight={300} overflow="auto">
                        <FormGroup>
                            {
                                (props.options && props?.options.length <= 0) &&
                                <Box
                                    display="flex"
                                    justifyContent="center"
                                    paddingY={2}
                                    gap={1}
                                    color="gray"
                                >
                                    <HelpIcon />
                                    <Typography>
                                        Nenhum resultado encontrado
                                    </Typography>
                                </Box>
                            }
                            {
                                (props.options && props?.options.length > 0) && props.options.map((i: any) => {
                                    let isCheck = false;
                                    if (props.multiple) isCheck = selected ? selected.find((row: any) => row.id === i.id) : false
                                    if (!props.multiple) isCheck = selected[0]?.id === i.id
                                    return (
                                        <FormControlLabel
                                            key={i.id}
                                            label={props.getLabel(i)}
                                            onClick={(e) => handleClick(i, e)}
                                            control={props.multiple ? <Checkbox checked={isCheck} /> : <Radio checked={isCheck} />}
                                        />
                                    )
                                })
                            }
                            {
                                props.limitData < props.totalData &&
                                <Box
                                    display="flex"
                                    justifyContent="center"
                                    alignItems="center"
                                    paddingY={2}
                                >
                                    {
                                        props.isFetching &&
                                        <CircularProgress size={24} />
                                    }
                                    {
                                        !props.isFetching &&
                                        <Button
                                            color="primary"
                                            variant="outlined"
                                            onClick={props.onEdgeReached}
                                        >Carregar mais</Button>
                                    }
                                </Box>
                            }
                        </FormGroup>
                    </Box>
                </DialogContent>
                <DialogActions>
                    {
                        props.permitirLimpar &&
                        <Button onClick={handleClear}>
                            Limpar
                        </Button>
                    }
                    <Button
                        type="button"
                        variant="contained"
                        onClick={handleSubmit}
                        disabled={selected.length <= 0}
                    >
                        Selecionar {props.multiple && `(${selected.length})`}
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    )
}


interface SelectWithModalProps {
    label: String;
    type?: "field" | "icon";
    emptyLabel: String;
    titleModal: string;
    fullWidthInput: boolean;
    permitirLimpar: boolean;

    options: any[];
    disabled: boolean;
    defaultSelected?: any;
    multiple: boolean;
    keywordValue?: string,
    isFetching: boolean
    totalData: number,
    limitData: number

    onChange: (values: any[]) => void;
    onClear: () => void;
    getLabel: (option: any) => string;
    onEdgeReached: () => void,
    onKeywordChange: (value: string) => void
}